import { useCallback, useMemo } from "react";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import {
  ColumnId,
  ColumnDef,
  COLUMNS_REGISTRY,
  DEFAULT_COLUMN_ORDER,
  isKnownColumnId,
} from "./callsColumnRegistry";

// ─── Schema version — bump to invalidate old configs ─────────────────────────
const SCHEMA_VERSION = 1;

// ─── Persisted shape ──────────────────────────────────────────────────────────

export type ColumnConfig = {
  columnId: ColumnId;
  visible: boolean;
  order: number;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build the localStorage key for column config.
 * Version is embedded in the key so older configs are naturally ignored.
 */
function buildStorageKey(userId: string): string {
  return `calls-column-config-v${SCHEMA_VERSION}-${userId}`;
}

/**
 * Build the default ColumnConfig[] from the registry.
 * Only user-configurable columns are included (actions column is excluded).
 * Visible = columns in DEFAULT_COLUMN_ORDER.
 */
function buildDefaultConfig(): ColumnConfig[] {
  return COLUMNS_REGISTRY.filter((c) => c.userConfigurable).map((col) => {
    const orderInDefault = DEFAULT_COLUMN_ORDER.indexOf(col.id);
    const isDefaultVisible = orderInDefault !== -1;
    return {
      columnId: col.id,
      visible: isDefaultVisible,
      order: isDefaultVisible ? orderInDefault : DEFAULT_COLUMN_ORDER.length,
    };
  });
}

/**
 * Module-level singleton default config — avoids rebuilding the array on every
 * hook render. Safe to share because `setConfig` always replaces the array
 * (never mutates), and `deserializeAndMerge` returns a new array.
 */
const DEFAULT_CONFIG: ColumnConfig[] = buildDefaultConfig();

/**
 * Deserialize and merge a raw value from localStorage into a valid ColumnConfig[].
 *
 * Rules (from spec):
 * 1. JSON parse error → return default config
 * 2. Unknown columnIds → ignored
 * 3. Missing default columns → appended at end with defaultVisible
 * 4. 0 visible columns → enforce min-1 (first default column forced visible)
 */
function deserializeAndMerge(raw: ColumnConfig[]): ColumnConfig[] {
  // Defensive: if localStorage was manually tampered (DevTools/extension)
  // with a non-array value, raw.filter(...) would crash. Fallback to defaults.
  if (!Array.isArray(raw)) return buildDefaultConfig();

  // Filter out unknown column IDs (whitelist defense)
  const known = raw.filter(
    (entry) =>
      isKnownColumnId(entry.columnId) &&
      typeof entry.visible === "boolean" &&
      typeof entry.order === "number"
  );

  // Build a map for quick lookup
  const knownMap = new Map(known.map((e) => [e.columnId, e]));

  // Find which user-configurable columns are missing from the saved config
  const allConfigurable = COLUMNS_REGISTRY.filter((c) => c.userConfigurable);
  const missing = allConfigurable.filter((c) => !knownMap.has(c.id));

  // Append missing columns at the end (after the known entries)
  const maxOrder = known.length > 0 ? Math.max(...known.map((e) => e.order)) : -1;
  const appended: ColumnConfig[] = missing.map((col, i) => ({
    columnId: col.id,
    visible: col.defaultVisible,
    order: maxOrder + 1 + i,
  }));

  const merged = [...known, ...appended];

  // Enforce min-1 visible
  const visibleCount = merged.filter((e) => e.visible).length;
  if (visibleCount === 0) {
    // Force first default column visible
    const firstDefault = DEFAULT_COLUMN_ORDER[0];
    const entry = merged.find((e) => e.columnId === firstDefault);
    if (entry) {
      entry.visible = true;
    } else if (merged.length > 0) {
      merged[0].visible = true;
    }
  }

  return merged;
}

/**
 * Get the ordered list of visible ColumnDef objects from a config.
 */
function getVisibleColumns(config: ColumnConfig[]): ColumnDef[] {
  const registryMap = new Map(COLUMNS_REGISTRY.map((c) => [c.id, c]));
  return [...config]
    .filter((e) => e.visible)
    .sort((a, b) => a.order - b.order)
    .map((e) => registryMap.get(e.columnId))
    .filter((c): c is ColumnDef => c !== undefined);
}

/**
 * Get all user-configurable ColumnDef objects in current order.
 */
function getAllConfigurableColumns(config: ColumnConfig[]): ColumnDef[] {
  const registryMap = new Map(COLUMNS_REGISTRY.map((c) => [c.id, c]));
  return [...config]
    .sort((a, b) => a.order - b.order)
    .map((e) => registryMap.get(e.columnId))
    .filter((c): c is ColumnDef => c !== undefined);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export type UseCallsColumnConfigResult = {
  /** Only visible columns, in user-configured order */
  visibleColumns: ColumnDef[];
  /** All user-configurable columns in current order (for panel) */
  allColumns: ColumnDef[];
  /** Toggle visibility of a column (min-1 guard: no-op if it would leave 0 visible) */
  toggleColumn: (id: ColumnId) => void;
  /** Reorder columns by providing a new ordered array of IDs */
  reorderColumns: (orderedIds: ColumnId[]) => void;
  /** Reset to default column order and visibility */
  resetToDefault: () => void;
};

export function useCallsColumnConfig(userId: string): UseCallsColumnConfigResult {
  const storageKey = buildStorageKey(userId);

  const [config, setConfig] = useLocalStorage<ColumnConfig[]>(storageKey, DEFAULT_CONFIG);

  // Merge memoized — only recompute when raw config changes
  // (handles stale localStorage from old schema)
  const effectiveConfig = useMemo(() => deserializeAndMerge(config), [config]);

  const visibleColumns = useMemo(() => getVisibleColumns(effectiveConfig), [effectiveConfig]);
  const allColumns = useMemo(() => getAllConfigurableColumns(effectiveConfig), [effectiveConfig]);

  const toggleColumn = useCallback(
    (id: ColumnId) => {
      const current = deserializeAndMerge(config);
      const target = current.find((e) => e.columnId === id);
      if (!target) return;

      // Min-1 guard: if toggling off and only 1 visible remains → no-op
      if (target.visible) {
        const visibleCount = current.filter((e) => e.visible).length;
        if (visibleCount <= 1) return;
      }

      const updated = current.map((e) =>
        e.columnId === id ? { ...e, visible: !e.visible } : e
      );
      setConfig(updated);
    },
    [config, setConfig]
  );

  const reorderColumns = useCallback(
    (orderedIds: ColumnId[]) => {
      const current = deserializeAndMerge(config);
      const updated = current.map((e) => {
        const newOrder = orderedIds.indexOf(e.columnId);
        return newOrder !== -1 ? { ...e, order: newOrder } : e;
      });
      setConfig(updated);
    },
    [config, setConfig]
  );

  const resetToDefault = useCallback(() => {
    setConfig(buildDefaultConfig());
  }, [setConfig]);

  return {
    visibleColumns,
    allColumns,
    toggleColumn,
    reorderColumns,
    resetToDefault,
  };
}
